import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { emailPattern } from 'src/email_pattern';
import { ToastService } from '../../services/toast/toast.service';

interface UserType {
  name;
  displayName;
}

@Component({
  selector: 'app-create-user',
  templateUrl: './create-user.component.html',
  styleUrls: ['./create-user.component.scss'],
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, TranslateModule, MatFormFieldModule, MatIconModule, MatSelectModule, MatDialogModule, MatInputModule, MatButtonModule],
})
export class CreateUserComponent implements OnInit, OnDestroy {
  userTypes: Array<UserType> = [
    { name: 'Admin', displayName: 'Admin' },
    { name: 'Writer', displayName: 'Writer' },
    { name: 'Reader', displayName: 'Reader' },
  ];

  i18nList = ['CreateUser.Admin', 'CreateUser.Writer', 'CreateUser.Reader'];

  createForm = this.fb.group({
    email: ['', [Validators.required, Validators.pattern(emailPattern)]],
    lastName: ['', [Validators.required]],
    firstName: ['', [Validators.required]],
    type: ['Reader', [Validators.required]],
  });
  buttonLock = false;
  translateSub: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private authService: AuthService,
    private toast: ToastService,
    private translate: TranslateService
  ) {
    this.translateSub = this.translate.onLangChange.subscribe((res) => {
      this.translateUserTypes();
    });
  }

  ngOnInit() {
    this.translateUserTypes();
  }

  ngOnDestroy() {
    this.translateSub.unsubscribe();
  }

  translateUserTypes() {
    this.translate.get(this.i18nList).subscribe((i18n) => {
      this.userTypes = this.userTypes.map((type: UserType) => {
        type.displayName = i18n[`CreateUser.${type.name}`];
        return type;
      });
    });
  }

  submit(value) {
    this.buttonLock = true;
    this.authService.createAccount(value).subscribe({
      complete: () => {
        this.createForm.reset({
          email: '',
          lastName: '',
          firstName: '',
          type: 'Reader',
        });
        this.buttonLock = false;
        this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', {
          duration: 2000,
        });
      },
      error: (err) => {
        this.buttonLock = false;
        const error = err.error;
        if (error.code) {
          if (error.code === 'auth/email-already-in-use') {
            this.toast.snackbar.open(this.translate.instant('Snackbar.RequestAlreadyPresent'), 'Ok', { duration: 2000 });
          } else if (error.code === 'CannotAddMoreUsersOnFreeApp') {
            this.toast.snackbar.open(this.translate.instant('Snackbar.CannotAddMoreUsersOnFreeApp'), 'Ok', { duration: 2000 });
          } else if (error.code === 'CannotAddMoreUsers') {
            this.toast.snackbar.open(this.translate.instant('Snackbar.CannotAddMoreUsers'), 'Ok', { duration: 2000 });
          } else if (error.code === 'UserAlreadyExists') {
            this.toast.snackbar.open(this.translate.instant('Snackbar.UserAlreadyExists'), 'Ok', { duration: 2000 });
          } else {
            this.toast.snackbar.open(error.code, 'Ok', { duration: 2000 });
          }
        } else {
          this.toast.snackbar.open(this.translate.instant('Snackbar.AccountCreationFailAdmin'), 'Ok', { duration: 2000 });
        }
      },
    });
  }
}
